import React, { useContext, useState, useRef, useEffect } from "react";
import { UserContext } from "../contexts/UserContext";
import UserPassport from "./UserPassport";
import { CreditCard } from "lucide-react";

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`;

const Profile = () => {
  const { user } = useContext(UserContext);
  const [activeTab, setActiveTab] = useState(null);
  const [showPassport, setShowPassport] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Signature canvas
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [currentSignature, setCurrentSignature] = useState(null);

  // Load existing signature on mount
  useEffect(() => {
    if (user?.user_signature) {
      // Remove data URL prefix if it exists (for backward compatibility)
      const cleanSignature = user.user_signature.replace(/^data:image\/png;base64,/, '');
      setCurrentSignature(cleanSignature);
      setHasSignature(true);
    }
  }, [user]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
  };

  // Signature canvas handlers
  const getCanvasCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    // Get client coordinates
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;

    // Calculate scale factor between displayed size and actual canvas size
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    // Apply scale to get actual canvas coordinates
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;

    return { x, y };
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';

    setIsDrawing(true);
    ctx.beginPath();

    const { x, y } = getCanvasCoordinates(e);
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();

    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getCanvasCoordinates(e);

    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const getBoundingBox = (ctx, width, height) => {
    const imageData = ctx.getImageData(0, 0, width, height);
    const pixels = imageData.data;

    let minX = width;
    let minY = height;
    let maxX = 0;
    let maxY = 0;

    // Scan all pixels to find the bounding box of non-white content
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const index = (y * width + x) * 4;
        const r = pixels[index];
        const g = pixels[index + 1];
        const b = pixels[index + 2];
        const a = pixels[index + 3];

        // Check if pixel is not white/transparent
        if ((a > 0 && (r < 255 || g < 255 || b < 255)) || (a < 255 && a > 0)) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }

    // If no content found, return null
    if (minX > maxX || minY > maxY) {
      return null;
    }

    return { minX, minY, maxX, maxY };
  };

  const saveSignature = async () => {
    if (!hasSignature) {
      setMessage('Please draw your signature first');
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Get bounding box of actual signature content
    const bbox = getBoundingBox(ctx, canvas.width, canvas.height);

    if (!bbox) {
      setMessage('Please draw your signature first');
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      // Add padding to the bounding box
      const padding = 5;
      const cropX = Math.max(0, bbox.minX - padding);
      const cropY = Math.max(0, bbox.minY - padding);
      const cropWidth = Math.min(canvas.width - cropX, bbox.maxX - bbox.minX + padding * 2);
      const cropHeight = Math.min(canvas.height - cropY, bbox.maxY - bbox.minY + padding * 2);

      // Target dimensions (with internal margins)
      const targetWidth = 300;
      const targetHeight = 100;
      const margin = 5;
      const maxWidth = targetWidth - margin * 2;
      const maxHeight = targetHeight - margin * 2;

      // Calculate scale to fit within maxWidth x maxHeight while maintaining aspect ratio
      const scaleX = maxWidth / cropWidth;
      const scaleY = maxHeight / cropHeight;
      const scale = Math.min(scaleX, scaleY);

      const scaledWidth = cropWidth * scale;
      const scaledHeight = cropHeight * scale;

      // Center the signature on the final canvas
      const offsetX = (targetWidth - scaledWidth) / 2;
      const offsetY = (targetHeight - scaledHeight) / 2;

      // Create final normalized canvas
      const finalCanvas = document.createElement('canvas');
      finalCanvas.width = targetWidth;
      finalCanvas.height = targetHeight;
      const finalCtx = finalCanvas.getContext('2d');

      // Fill with white background
      finalCtx.fillStyle = '#FFFFFF';
      finalCtx.fillRect(0, 0, targetWidth, targetHeight);

      // Draw the cropped and scaled signature centered
      finalCtx.drawImage(
        canvas,
        cropX, cropY, cropWidth, cropHeight,
        offsetX, offsetY, scaledWidth, scaledHeight
      );

      // Convert to base64 PNG and remove the data:image/png;base64, prefix
      const dataUrl = finalCanvas.toDataURL('image/png');
      const base64Signature = dataUrl.replace(/^data:image\/png;base64,/, '');

      // Save to backend
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/users/${user.id}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          user_signature: base64Signature
        })
      });

      if (response.ok) {
        setCurrentSignature(base64Signature);
        setMessage('Signature saved successfully');
      } else {
        const errorData = await response.json();
        setMessage(errorData.message || 'Error saving signature');
      }
    } catch (error) {
      console.error('Signature save error:', error);
      setMessage(`Server connection error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteSignature = async () => {
    if (!currentSignature) {
      return;
    }

    if (!window.confirm('Are you sure you want to delete your current signature?')) {
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/users/${user.id}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          user_signature: null
        })
      });

      if (response.ok) {
        setCurrentSignature(null);
        setMessage('Signature deleted successfully');
      } else {
        const errorData = await response.json();
        setMessage(errorData.message || 'Error deleting signature');
      }
    } catch (error) {
      console.error('Signature delete error:', error);
      setMessage(`Server connection error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setMessage('New password must be at least 6 characters');
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/change-password/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword
        })
      });

      if (response.ok) {
        setMessage('Password changed successfully');
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        const errorData = await response.json();
        setMessage(errorData.message || 'Error changing password');
      }
    } catch (error) {
      console.error('Password change error:', error);
      setMessage(`Server connection error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white shadow-md rounded-lg p-6 max-w-3xl mx-auto mt-10">
      {/* Header */}
      <div className="text-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">
          {getGreeting()}, {user?.firstName || "User"}!
        </h1>
        <p className="text-gray-600 mt-2">Nice to see you in the system.</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => {
              setActiveTab('password');
              setMessage('');
            }}
            className={`${
              activeTab === 'password'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
          >
            Change Password
          </button>
          <button
            onClick={() => {
              setActiveTab('signature');
              setMessage('');
            }}
            className={`${
              activeTab === 'signature'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
          >
            Set Your Signature
          </button>
          <button
            onClick={() => {
              setShowPassport(true);
              setMessage('');
            }}
            className="border-transparent text-purple-500 hover:text-purple-700 hover:border-purple-300 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2"
          >
            <CreditCard className="w-4 h-4" />
            My Powerpass
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {!activeTab && (
          <div className="text-center py-8">
            <div className="mb-4">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Profile Settings</h3>
            <p className="text-gray-600 mb-4">Select a tab above to manage your profile settings</p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => setActiveTab('password')}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
              >
                Change Password
              </button>
              <button
                onClick={() => setActiveTab('signature')}
                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
              >
                Set Signature
              </button>
            </div>
          </div>
        )}

        {activeTab === 'password' && (
          <div>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Change Password</h2>

            {message && (
              <div className={`mb-4 p-3 rounded ${message.includes('successfully') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {message}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password
                </label>
                <input
                  type="password"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  minLength="6"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  minLength="6"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Changing...' : 'Change Password'}
              </button>
            </form>
          </div>
        )}

        {activeTab === 'signature' && (
          <div>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Set Your Signature</h2>

            {message && (
              <div className={`mb-4 p-3 rounded ${message.includes('successfully') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {message}
              </div>
            )}

            <div className="space-y-4">
              {/* Current Signature Preview */}
              {currentSignature && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Signature:
                  </label>
                  <div className="border border-gray-300 rounded-md p-4 bg-gray-50">
                    <img
                      src={`data:image/png;base64,${currentSignature}`}
                      alt="Current Signature"
                      className="max-w-full h-auto"
                      style={{ maxHeight: '100px' }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={deleteSignature}
                    disabled={isLoading}
                    className="mt-2 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Deleting...' : 'Delete Current Signature'}
                  </button>
                </div>
              )}

              {/* Drawing Canvas */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Draw Your Signature:
                </label>
                <div className="border-2 border-gray-300 rounded-md overflow-hidden bg-white">
                  <canvas
                    ref={canvasRef}
                    width={600}
                    height={200}
                    className="w-full cursor-crosshair touch-none"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Draw your signature above. It will be automatically cropped, scaled to maintain proportions, and normalized to 300x100 pixels for consistent display.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={clearSignature}
                  className="flex-1 bg-gray-500 text-white py-2 px-4 rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={saveSignature}
                  disabled={isLoading || !hasSignature}
                  className="flex-1 bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Saving...' : 'Save Signature'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Passport Modal */}
      {showPassport && (
        <UserPassport onClose={() => setShowPassport(false)} />
      )}
    </div>
  );
};

export default Profile;
